import React, { useEffect, useMemo, useState } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { Box, Skeleton, Alert } from '@mui/material';
import { api } from '../../../../services/Main/Api';
import { getCondoId } from '../../../../services/Main/AuthStorage';

const brl = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
});

const percentFmt = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});

const formatBRLFromCents = (valueInCents) =>
    brl.format(Number(valueInCents || 0) / 100);

// Eixo Y -> compacto (k/M) em reais.
const yAxisValueFormatter = (valueInCents) => {
    const valueInReais = Number(valueInCents || 0) / 100;
    const abs = Math.abs(valueInReais);

    if (abs >= 1_000_000) {
        return `${(valueInReais / 1_000_000).toFixed(1).replace('.', ',')}M`;
    }
    if (abs >= 1_000) {
        return `${(valueInReais / 1_000).toFixed(1).replace('.', ',')}k`;
    }
    return `${Math.round(valueInReais)}`;
};

const chartSetting = { height: 400 };

export default function TransactionsChart() {
    const [dataset, setDataset] = useState([]);
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');

                const condoId = getCondoId?.();
                const params = {};
                // if (condoId) params.condo_id = condoId;

                const res = await api.get('analytics/transactions-dataset/', { params });

                if (!alive) return;

                setYears(res?.data?.years ?? []);
                setDataset(res?.data?.dataset ?? []);
            } catch (e) {
                if (!alive) return;
                setError('Não foi possível carregar o gráfico de transações.');
                setYears([]);
                setDataset([]);
            } finally {
                if (!alive) return;
                setLoading(false);
            }
        };

        fetchData();
        return () => {
            alive = false;
        };
    }, []);

    const series = useMemo(() => {
        if (!years?.length) return [];

        const latestYear = Math.max(...years);
        const prevYear = years.includes(latestYear - 1) ? latestYear - 1 : null;

        return years.map((y) => ({
            dataKey: `year${y}`,
            label: String(y),

            // Tooltip: R$ + % crescimento (apenas no ano mais recente)
            valueFormatter: (valueInCents, context) => {
                const valueText = formatBRLFromCents(valueInCents);

                // só adiciona crescimento para o último ano e se existir ano anterior
                if (y !== latestYear || !prevYear) return valueText;

                const row = dataset?.[context?.dataIndex];
                const baseCents = Number(row?.[`year${prevYear}`] || 0);
                const currCents = Number(row?.[`year${latestYear}`] || 0);

                if (baseCents <= 0) return `${valueText} (—)`;

                const growth = (currCents - baseCents) / baseCents; // ex.: 0.234
                const sign = growth >= 0 ? '+' : '';
                const pctText = `${sign}${percentFmt.format(growth * 100)}%`;

                return `${valueText} (${pctText})`;
            },
        }));
    }, [years, dataset]);

    if (loading) {
        return (
            <Box>
                <Skeleton variant="rounded" height={320} />
            </Box>
        );
    }

    if (error) return <Alert severity="warning">{error}</Alert>;

    if (!dataset?.length || !series?.length) {
        return <Alert severity="info">Sem dados para exibir no período.</Alert>;
    }

    return (
        <BarChart
            dataset={dataset}
            xAxis={[{ dataKey: 'month', scaleType: 'band' }]}
            yAxis={[{ valueFormatter: yAxisValueFormatter }]}
            series={series}
            {...chartSetting}
        />
    );
}
